import React from "react";
import Preloader from "../../../Preloader/Preloader";
import {
  ApprovalStatusBadge,
  getApprovalStatusText,
} from "./paymentsApprovalStatus";

/**
 * Деньги по секундам отчётов, опираясь только на поля бэкенда:
 * labour_cost — бюджет трудозатрат в секундах (на всю смету amount).
 * Тогда «цена секунды» = amount / labour_cost → сумма человека = seconds × (amount / labour_cost).
 */
const moneyFromSecondsUsingLabourCost = (secondsSpend, amountTg, labourCostSeconds) => {
  const seconds = Number(secondsSpend) || 0;
  const amount = Number(amountTg);
  const labour = Number(labourCostSeconds);
  if (!labour || labour <= 0 || Number.isNaN(labour) || !amount || amount <= 0 || Number.isNaN(amount)) {
    return null;
  }
  return seconds * (amount / labour);
};

const GeneralDesignTable = ({
  generalDesignLoading,
  generalDesignError,
  generalDesignRows,
  expandedGeneralProjects,
  generalUsersByContract,
  loadingGeneralUsers,
  expandedGeneralUsers,
  generalReportsByKey,
  loadingGeneralReports,
  toggleGeneralProject,
  toggleGeneralUser,
  savingId,
  setSavingId,
  setConfirmModal,
  setCommentModal,
  updateGeneralDesignCorrection,
  setGeneralReportsByKey,
  formatMoney,
  month,
  year,
  approvalBySectionId = {},
  isSectionOnApproval = () => false,
  canDeleteApprovalForSection = () => false,
  deleteApprovalForSection = () => {},
  deletingApproval = false,
  sectionTotalsState = {},
  isReadOnly = false,
}) => (
  <div className="pp-table-wrap">
    <table className="pp-table">
      <thead>
        <tr>
          <th>Проект</th>
          <th
            className="pp-money pp-user-list-sum-col"
            title="Сумма «К выплате» по корректировкам всех сотрудников этого раздела за месяц"
          >
            К выплате (итого)
          </th>
        </tr>
      </thead>
      <tbody>
        {generalDesignLoading ? (
          <tr>
            <td className="pp-loading-cell" colSpan={2}>
              <Preloader />
            </td>
          </tr>
        ) : generalDesignError ? (
          <tr>
            <td className="pp-empty" colSpan={2}>
              {generalDesignError}
            </td>
          </tr>
        ) : generalDesignRows.length === 0 ? (
          <tr>
            <td className="pp-empty" colSpan={2}>
              Нет данных Генплан РП за выбранный период
            </td>
          </tr>
        ) : (
          generalDesignRows.map((row) => {
            const rowProjectName = row?.project?.name || `ID ${row?.project_id || "—"}`;
            const rowSectionName = row?.section?.section?.name || "—";
            const contractSectionsId = Number(row?.contract_sections_id);
            const isProjectExpanded =
              !Number.isNaN(contractSectionsId) &&
              expandedGeneralProjects.has(contractSectionsId);
            const users = generalUsersByContract[contractSectionsId];
            const isLoadingUsers = loadingGeneralUsers[contractSectionsId];
            const sectionApproval = !Number.isNaN(contractSectionsId)
              ? approvalBySectionId[contractSectionsId] ?? null
              : null;
            const isLockedByApproval =
              !Number.isNaN(contractSectionsId) &&
              isSectionOnApproval(contractSectionsId);
            const approvalStatusText = sectionApproval
              ? getApprovalStatusText(sectionApproval)
              : "Не отправлено";
            const canDeleteSectionApproval =
              !Number.isNaN(contractSectionsId) &&
              !isReadOnly &&
              canDeleteApprovalForSection(contractSectionsId);
            const projectLabel = `${rowProjectName} — ${rowSectionName}`;

            const sectionState = !Number.isNaN(contractSectionsId)
              ? sectionTotalsState[contractSectionsId]
              : undefined;
            const sectionUsersForTotal = !Number.isNaN(contractSectionsId)
              ? generalUsersByContract[contractSectionsId]
              : undefined;
            const sectionCorrectionTotal = Array.isArray(sectionUsersForTotal)
              ? sectionUsersForTotal.reduce((sum, u) => {
                  const data =
                    generalReportsByKey[`${contractSectionsId}-${u.id}`];
                  const corrSum = Number(data?.correction?.completion_sum_after_correction);
                  return Number.isFinite(corrSum) ? sum + corrSum : sum;
                }, 0)
              : null;
            const isSectionTotalLoading =
              sectionState === "loading" || sectionState === undefined;
            const sectionTotalHasError = sectionState === "error";

            return (
              <React.Fragment
                key={`${row?.contract_sections_id || "cs"}-${row?.project_id || row?.project?.id || "p"}`}
              >
                <tr
                  className="pp-user-row"
                  onClick={() => {
                    if (!Number.isNaN(contractSectionsId)) {
                      toggleGeneralProject(contractSectionsId);
                    }
                  }}
                >
                  <td>
                    {!Number.isNaN(contractSectionsId) && (
                      <span
                        className={`pp-expand ${isProjectExpanded ? "expanded" : ""}`}
                        aria-hidden
                      >
                        ›
                      </span>
                    )}
                    {rowProjectName}
                    <ApprovalStatusBadge
                      approval={sectionApproval}
                      approvalStatusText={approvalStatusText}
                    />
                    {isLockedByApproval && (
                      <span
                        className="pp-lock-hint"
                        data-tooltip="Редактирование заблокировано: раздел на согласовании"
                        aria-label="Редактирование заблокировано"
                      >
                        🔒
                      </span>
                    )}
                    {canDeleteSectionApproval && (
                      <button
                        type="button"
                        className="pp-row-delete-approval-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteApprovalForSection(contractSectionsId, projectLabel);
                        }}
                        disabled={deletingApproval}
                        title="Удалить согласование"
                      >
                        Удалить
                      </button>
                    )}
                  </td>
                  <td
                    className="pp-money pp-user-row-total"
                    onClick={(e) => e.stopPropagation()}
                    title={
                      sectionCorrectionTotal != null
                        ? `Сумма «К выплате» по корректировкам всех сотрудников: ₸ ${formatMoney(sectionCorrectionTotal)}`
                        : undefined
                    }
                  >
                    {sectionTotalHasError ? (
                      "–"
                    ) : isSectionTotalLoading && sectionCorrectionTotal == null ? (
                      <span className="pp-row-total-muted">…</span>
                    ) : sectionCorrectionTotal == null ? (
                      "–"
                    ) : (
                      <>₸ {formatMoney(sectionCorrectionTotal)}</>
                    )}
                  </td>
                </tr>
                {isProjectExpanded && (
                  <tr className="pp-detail-row">
                    <td className="pp-detail-cell" colSpan={2}>
                      {isLoadingUsers ? (
                        <div className="pp-loading-cell">
                          <Preloader />
                        </div>
                      ) : !Array.isArray(users) || users.length === 0 ? (
                        <div className="pp-empty">Нет сотрудников с отчетами за период</div>
                      ) : (
                        <table className="pp-detail-table">
                          <tbody>
                            {users.map((user) => {
                              const userKey = `${contractSectionsId}-${user.id}`;
                              const isUserExpanded = expandedGeneralUsers.has(userKey);
                              const userReportsData = generalReportsByKey[userKey];
                              const correction = userReportsData?.correction || {};
                              const loadingReports = loadingGeneralReports[userKey];
                              const userName =
                                [user.surname, user.name].filter(Boolean).join(" ") ||
                                `Специалист ID ${user.id}`;
                              return (
                                <React.Fragment key={userKey}>
                                  <tr
                                    className="pp-project-row"
                                    onClick={() => toggleGeneralUser(contractSectionsId, user.id)}
                                  >
                                    <td colSpan={10}>
                                      <span
                                        className={`pp-expand ${isUserExpanded ? "expanded" : ""}`}
                                        aria-hidden
                                      >
                                        ›
                                      </span>
                                      {userName}
                                    </td>
                                  </tr>
                                  {isUserExpanded && (
                                    <>
                                      {loadingReports ? (
                                        <tr>
                                          <td colSpan={10} className="pp-loading-cell">
                                            <Preloader />
                                          </td>
                                        </tr>
                                      ) : (
                                        <>
                                          <tr className="pp-detail-header-row">
                                            <th>Раздел</th>
                                            <th className="pp-money">Смета по договору</th>
                                            <th className="pp-money">Выплачено</th>
                                            <th className="pp-money">Остаток</th>
                                            <th>% доля сотрудника</th>
                                            <th>% выполнения (по данным от сотрудника)</th>
                                            <th className="pp-money">К выплате (по данным от сотрудника)</th>
                                            <th>% выполнения (корректировка руководителя)</th>
                                            <th className="pp-money">К выплате (корректировка руководителя)</th>
                                            <th>Комментарий</th>
                                          </tr>
                                          {(() => {
                                            const correctionPct =
                                              correction?.completion_percentage_after_correction ?? null;
                                            const correctionSum =
                                              correction?.completion_sum_after_correction ?? null;
                                            const correctionComment = correction?.comment || "—";
                                            const correctionSectionName = rowSectionName || "Корректировка";
                                            const estimateContract =
                                              row?.amount ?? row?.section?.amount ?? 0;
                                            const alreadyPayed = row?.already_payed;
                                            const labourBudgetSeconds = Number(row?.labour_cost);
                                            const reports = Array.isArray(userReportsData?.reports)
                                              ? userReportsData.reports
                                              : [];
                                            const totalSecondsByEmployee = reports.reduce(
                                              (sum, report) => sum + (Number(report?.seconds_spend) || 0),
                                              0,
                                            );
                                            const toBePayedByEmployee = moneyFromSecondsUsingLabourCost(
                                              totalSecondsByEmployee,
                                              estimateContract,
                                              labourBudgetSeconds,
                                            );
                                            const completionPercentByEmployee =
                                              toBePayedByEmployee != null &&
                                              Number(estimateContract) > 0
                                                ? (toBePayedByEmployee / Number(estimateContract)) * 100
                                                : null;
                                            const estimateContractNum = Number(estimateContract) || 0;
                                            const alreadyPayedNum = Number(alreadyPayed) || 0;
                                            const remainingAmount = estimateContractNum - alreadyPayedNum;
                                            const toBePayedByEmployeeNum = Number(toBePayedByEmployee);
                                            const correctionSumNum = Number(correctionSum);
                                            const isEmployeePayoutOverBudget =
                                              estimateContractNum > 0 &&
                                              Number.isFinite(toBePayedByEmployeeNum) &&
                                              alreadyPayedNum + toBePayedByEmployeeNum > estimateContractNum;
                                            const isCorrectionPayoutOverBudget =
                                              estimateContractNum > 0 &&
                                              Number.isFinite(correctionSumNum) &&
                                              alreadyPayedNum + correctionSumNum > estimateContractNum;
                                            const maxAllowedPayout = Math.max(
                                              0,
                                              estimateContractNum - alreadyPayedNum,
                                            );
                                            const isSavingGeneralCorrection =
                                              savingId ===
                                              `general-design-${contractSectionsId}-${user.id}`;

                                            return (
                                              <tr className="pp-work-row">
                                                <td>{correctionSectionName}</td>
                                                <td className="pp-money">₸ {formatMoney(estimateContract)}</td>
                                                <td className="pp-money">
                                                  {alreadyPayed == null
                                                    ? "—"
                                                    : `₸ ${formatMoney(alreadyPayed)}`}
                                                </td>
                                                <td className="pp-money">
                                                  ₸ {formatMoney(remainingAmount)}
                                                </td>
                                                <td>—</td>
                                                <td>
                                                  {completionPercentByEmployee == null
                                                    ? "—"
                                                    : `${completionPercentByEmployee.toFixed(2)}%`}
                                                </td>
                                                <td
                                                  className="pp-money"
                                                  style={
                                                    isEmployeePayoutOverBudget
                                                      ? { color: "#d32f2f", fontWeight: 600 }
                                                      : undefined
                                                  }
                                                  title={
                                                    isEmployeePayoutOverBudget
                                                      ? "Превышение: Выплачено + К выплате больше суммы по договору"
                                                      : undefined
                                                  }
                                                >
                                                  {toBePayedByEmployee == null
                                                    ? "—"
                                                    : `₸ ${formatMoney(toBePayedByEmployee)}`}
                                                  {isEmployeePayoutOverBudget && (
                                                    <div style={{ color: "#d32f2f", fontWeight: 600 }}>
                                                      Макс: ₸ {formatMoney(maxAllowedPayout)}
                                                    </div>
                                                  )}
                                                </td>
                                                <td>
                                                  {correctionPct == null ? "—" : `${correctionPct}%`}
                                                  {!isReadOnly && (
                                                    <button
                                                      type="button"
                                                      className="pp-edit-btn"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setConfirmModal({
                                                          open: true,
                                                          title: "Изменить процент выполнения?",
                                                          type: "percent",
                                                          correctionId: contractSectionsId,
                                                          userId: user.id,
                                                          userName,
                                                          workTypeName: correctionSectionName,
                                                          currentValue: String(correctionPct ?? 0),
                                                          newValue: String(correctionPct ?? 0),
                                                          estimateContract: Number(estimateContract) || 0,
                                                          sharePercent: 100,
                                                          onConfirm: (newVal) => {
                                                            const payload = {
                                                              contract_sections_id: contractSectionsId,
                                                              user_id: Number(user.id),
                                                              month,
                                                              year,
                                                              completion_percentage_after_correction: Number(newVal),
                                                            };
                                                            setSavingId(
                                                              `general-design-${contractSectionsId}-${user.id}`,
                                                            );
                                                            updateGeneralDesignCorrection(payload)
                                                              .then((updatedCorrection) => {
                                                                setGeneralReportsByKey((prev) => ({
                                                                  ...prev,
                                                                  [userKey]: {
                                                                    correction: {
                                                                      ...(prev[userKey]?.correction || {}),
                                                                      ...updatedCorrection,
                                                                    },
                                                                    reports: prev[userKey]?.reports || [],
                                                                  },
                                                                }));
                                                              })
                                                              .catch((err) =>
                                                                alert(
                                                                  err?.message ||
                                                                    "Ошибка сохранения корректировки",
                                                                ),
                                                              )
                                                              .finally(() => setSavingId(null));
                                                          },
                                                        });
                                                      }}
                                                      disabled={isSavingGeneralCorrection || isLockedByApproval}
                                                      title={
                                                        isLockedByApproval
                                                          ? "Раздел отправлен на согласование — редактирование запрещено"
                                                          : "Изменить процент"
                                                      }
                                                    >
                                                      ✎
                                                    </button>
                                                  )}
                                                </td>
                                                <td
                                                  className="pp-money"
                                                  style={
                                                    isCorrectionPayoutOverBudget
                                                      ? { color: "#d32f2f", fontWeight: 600 }
                                                      : undefined
                                                  }
                                                  title={
                                                    isCorrectionPayoutOverBudget
                                                      ? "Превышение: Выплачено + К выплате больше суммы по договору"
                                                      : undefined
                                                  }
                                                >
                                                  {correctionSum == null
                                                    ? "—"
                                                    : `₸ ${formatMoney(correctionSum)}`}
                                                  {isCorrectionPayoutOverBudget && (
                                                    <div style={{ color: "#d32f2f", fontWeight: 600 }}>
                                                      Макс: ₸ {formatMoney(maxAllowedPayout)}
                                                    </div>
                                                  )}
                                                  {!isReadOnly && (
                                                    <button
                                                      type="button"
                                                      className="pp-edit-btn"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setConfirmModal({
                                                          open: true,
                                                          title: "Изменить сумму к выплате?",
                                                          type: "sum",
                                                          correctionId: contractSectionsId,
                                                          userId: user.id,
                                                          userName,
                                                          workTypeName: correctionSectionName,
                                                          currentValue: String(correctionSum ?? 0),
                                                          newValue: String(correctionSum ?? 0),
                                                          estimateContract: Number(estimateContract) || 0,
                                                          sharePercent: 100,
                                                          onConfirm: (newVal) => {
                                                            const payload = {
                                                              contract_sections_id: contractSectionsId,
                                                              user_id: Number(user.id),
                                                              month,
                                                              year,
                                                              completion_sum_after_correction: Number(newVal),
                                                            };
                                                            setSavingId(
                                                              `general-design-${contractSectionsId}-${user.id}`,
                                                            );
                                                            updateGeneralDesignCorrection(payload)
                                                              .then((updatedCorrection) => {
                                                                setGeneralReportsByKey((prev) => ({
                                                                  ...prev,
                                                                  [userKey]: {
                                                                    correction: {
                                                                      ...(prev[userKey]?.correction || {}),
                                                                      ...updatedCorrection,
                                                                    },
                                                                    reports: prev[userKey]?.reports || [],
                                                                  },
                                                                }));
                                                              })
                                                              .catch((err) =>
                                                                alert(
                                                                  err?.message ||
                                                                    "Ошибка сохранения корректировки",
                                                                ),
                                                              )
                                                              .finally(() => setSavingId(null));
                                                          },
                                                        });
                                                      }}
                                                      disabled={isSavingGeneralCorrection || isLockedByApproval}
                                                      title={
                                                        isLockedByApproval
                                                          ? "Раздел отправлен на согласование — редактирование запрещено"
                                                          : "Изменить сумму"
                                                      }
                                                    >
                                                      ✎
                                                    </button>
                                                  )}
                                                </td>
                                                <td>
                                                  {correctionComment}
                                                  {!isReadOnly && (
                                                    <button
                                                      type="button"
                                                      className="pp-edit-btn"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setCommentModal({
                                                          open: true,
                                                          workTypeName: `${userName} — ${correctionSectionName}`,
                                                          correctionId: contractSectionsId,
                                                          currentComment: correction?.comment || "",
                                                          onSave: (commentValue) => {
                                                            const payload = {
                                                              contract_sections_id: contractSectionsId,
                                                              user_id: Number(user.id),
                                                              month,
                                                              year,
                                                              comment: String(commentValue || ""),
                                                            };
                                                            setSavingId(
                                                              `general-design-${contractSectionsId}-${user.id}`,
                                                            );
                                                            updateGeneralDesignCorrection(payload)
                                                              .then((updatedCorrection) => {
                                                                setGeneralReportsByKey((prev) => ({
                                                                  ...prev,
                                                                  [userKey]: {
                                                                    correction: {
                                                                      ...(prev[userKey]?.correction || {}),
                                                                      ...updatedCorrection,
                                                                    },
                                                                    reports: prev[userKey]?.reports || [],
                                                                  },
                                                                }));
                                                              })
                                                              .catch((err) =>
                                                                alert(
                                                                  err?.message ||
                                                                    "Ошибка сохранения корректировки",
                                                                ),
                                                              )
                                                              .finally(() => setSavingId(null));
                                                          },
                                                        });
                                                      }}
                                                      disabled={isSavingGeneralCorrection || isLockedByApproval}
                                                      title={
                                                        isLockedByApproval
                                                          ? "Раздел отправлен на согласование — редактирование запрещено"
                                                          : "Комментарий"
                                                      }
                                                    >
                                                      ✎
                                                    </button>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })()}
                                        </>
                                      )}
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })
        )}
      </tbody>
    </table>
  </div>
);

export default GeneralDesignTable;
